import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    IconButton,
    Typography,
    Alert,
    CircularProgress
} from '@mui/material';
import { Close as CloseIcon, Print as PrintIcon } from '@mui/icons-material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Cell
} from 'recharts';
import { useReactToPrint } from 'react-to-print';
import { getProgramUserRatingsByProgramCode, getDropdownData } from '../Queries';
import PrintRatingReport from './PrintRatingReport';

const ResultsChartDialog = ({ open, onClose, user, programCode, clientCode }) => {
    const [chartData, setChartData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const printRef = useRef();

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `Individual_Report_${user?.name?.replace(/\s+/g, '_')}_${new Date().toLocaleDateString()}`,
    });

    useEffect(() => {
        if (open && user && programCode) {
            loadResultsData();
        }
    }, [open, user, programCode]);

    const loadResultsData = async () => {
        setIsLoading(true);
        setError(null);
        try {
            // Load rating metrics
            const allDropdownData = await getDropdownData();
            const metrics = allDropdownData.filter(
                item => item.type === 'program_rating_metrics' && item.client_code === clientCode
            );

            if (metrics.length === 0) {
                setError('No rating metrics configured for this program');
                setIsLoading(false);
                return;
            }

            // Load all ratings for the program
            const allRatings = await getProgramUserRatingsByProgramCode(programCode);

            // Filter ratings for the specific user - check both user_id and id fields
            const userRatings = allRatings.filter(rating => {
                return rating.user_id === user.email;
            });

            if (userRatings.length === 0) {
                setError('No rating data available for this user');
                setIsLoading(false);
                return;
            }

            // Extract metric names from the first metric configuration
            const metricNames = metrics.map(m => m.name);

            // Calculate data for each metric
            const data = [];
            
            metricNames.forEach(metricName => {
                let userTotalScore = 0;
                let userCount = 0;
                let teamTotalScore = 0;
                let teamCount = 0;

                // Calculate user's scores across all sessions
                userRatings.forEach(rating => {
                    try {
                        const ratingJson = rating.rating_json;
                        
                        if (ratingJson && ratingJson[metricName] && ratingJson[metricName].rating) {
                            const ratingValue = parseInt(ratingJson[metricName].rating);
                            if (!isNaN(ratingValue)) {
                                userTotalScore += ratingValue;
                                userCount++;
                            }
                        }
                    } catch (e) {
                        console.error('Error parsing user rating:', e, rating);
                    }
                });

                // Calculate team average across all users and all sessions
                allRatings.forEach(rating => {
                    try {
                        const ratingJson = rating.rating_json;
                        
                        if (ratingJson && ratingJson[metricName] && ratingJson[metricName].rating) {
                            const ratingValue = parseInt(ratingJson[metricName].rating);
                            if (!isNaN(ratingValue)) {
                                teamTotalScore += ratingValue;
                                teamCount++;
                            }
                        }
                    } catch (e) {
                        console.error('Error parsing team rating:', e, rating);
                    }
                });

                if (userCount > 0 || teamCount > 0) {
                    data.push({
                        metric: metricName,
                        'Initial': userCount > 0 ? parseFloat((userTotalScore / userCount).toFixed(2)) : 0,
                        'Team Average': teamCount > 0 ? parseFloat((teamTotalScore / teamCount).toFixed(2)) : 0
                    });
                }
            });

            if (data.length === 0) {
                setError('No rating data available');
            } else {
                setChartData(data);
            }
        } catch (error) {
            console.error('Error loading results data:', error);
            setError('Failed to load results data');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setChartData([]);
        setError(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    fontFamily: 'Poppins',
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle sx={{
                borderBottom: '1px solid #e0e0e0',
                pb: 2,
                fontFamily: 'Poppins',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Box>
                    <Typography variant="h6" sx={{ fontFamily: 'Poppins', fontWeight: 600 }}>
                        Results Overview
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {user?.name} ({user?.email})
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3, mt: 2, overflow: 'hidden' }}>
                {isLoading ? (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: '300px' 
                    }}>
                        <CircularProgress size={50} sx={{ color: '#0a0068' }} />
                        <Typography sx={{ mt: 2, fontFamily: 'Poppins', color: '#666' }}>
                            Loading results data...
                        </Typography>
                    </Box>
                ) : error ? (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: '300px' 
                    }}>
                        <Alert severity="info" sx={{ fontFamily: 'Poppins' }}>
                            {error}
                        </Alert>
                    </Box>
                ) : (
                    <Box>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontFamily: 'Poppins', 
                                mb: 2, 
                                color: '#666',
                                textAlign: 'center'
                            }}
                        >
                            Average scores across all completed sessions (Initial, Final)
                        </Typography>
                        <ResponsiveContainer width="100%" height={350}>
                            <BarChart
                                data={chartData}
                                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="metric" 
                                    angle={0}
                                    textAnchor="middle"
                                    height={80}
                                    interval={0}
                                    tick={(props) => {
                                        const { x, y, payload } = props;
                                        const words = payload.value.split(' ');
                                        return (
                                            <g transform={`translate(${x},${y})`}>
                                                {words.map((word, index) => (
                                                    <text
                                                        key={index}
                                                        x={0}
                                                        y={index * 14}
                                                        dy={8}
                                                        textAnchor="middle"
                                                        fill="#666"
                                                        fontSize="11px"
                                                        fontFamily="Poppins"
                                                    >
                                                        {word}
                                                    </text>
                                                ))}
                                            </g>
                                        );
                                    }}
                                />
                                <YAxis 
                                    domain={[0, 5]}
                                    ticks={[0, 1, 2, 3, 4, 5]}
                                    style={{ 
                                        fontSize: '12px',
                                        fontFamily: 'Poppins'
                                    }}
                                />
                                <Tooltip 
                                    contentStyle={{ 
                                        fontFamily: 'Poppins',
                                        borderRadius: '8px',
                                        border: '1px solid #e0e0e0'
                                    }}
                                />
                                <Legend 
                                    wrapperStyle={{ 
                                        fontFamily: 'Poppins',
                                        paddingTop: '20px'
                                    }}
                                />
                                <Bar 
                                    dataKey="Initial" 
                                    fill="#5DADE2"
                                    radius={[8, 8, 0, 0]}
                                />
                                <Bar 
                                    dataKey="Team Average" 
                                    fill="#1A237E"
                                    radius={[8, 8, 0, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                    onClick={handlePrint}
                    startIcon={<PrintIcon />}
                    disabled={isLoading || error || chartData.length === 0}
                    sx={{
                        textTransform: 'none',
                        fontFamily: 'Poppins',
                        background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
                        color: '#ffffff',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #0c0082 0%, #d63550 100%)',
                        },
                        '&:disabled': {
                            background: '#e0e0e0',
                            color: '#999'
                        }
                    }}
                >
                    Print Report
                </Button>
                <Button
                    onClick={handleClose}
                    sx={{
                        textTransform: 'none',
                        fontFamily: 'Poppins',
                        color: '#666'
                    }}
                >
                    Close
                </Button>
            </DialogActions>
            
            {/* Hidden Print Component */}
            <Box sx={{ display: 'none' }}>
                <PrintRatingReport
                    ref={printRef}
                    users={user ? [user] : []}
                    programCode={programCode}
                    clientCode={clientCode}
                    reportType="initial"
                />
            </Box>
        </Dialog>
    );
};

export default ResultsChartDialog;
